import { loadNativeModule, pickMethod, describeError } from './loadNativeModule';

// Shared across getWifiStatus/getEthernetLink so the module loads once per run.
// Scoped to a single run via resetNetworkStatusCache() — a re-run must rebuild to
// read live state, not the previous run's cached readings.
let cachedInstance = null;

export function resetNetworkStatusCache () {
    cachedInstance = null;
}

function getNetworkStatusInstance () {
    if (cachedInstance) return cachedInstance;
    const NetworkStatus = loadNativeModule('@brightsign/networkstatus');
    if (!NetworkStatus) return null;
    cachedInstance = new NetworkStatus();
    return cachedInstance;
}

export async function getWifiStatus (iface = 'wlan0') {
    const instance = getNetworkStatusInstance();
    if (!instance) return { available: false, reason: 'networkstatus module unavailable' };

    try {
        const getWifiInterfaceStatus = pickMethod(instance, ['getInterfaceWifiStatus', 'getinterfacewifistatus']);
        if (!getWifiInterfaceStatus) {
            return { available: false, reason: 'networkstatus getters unavailable on this firmware' };
        }

        const status = await getWifiInterfaceStatus(iface);
        return {
            available: true,
            connected: Boolean(status && status.connected),
            signalStrength: status && typeof status.signalStrength === 'number' ? status.signalStrength : null,
            essid: (status && status.essid) || null
        };
    } catch (error) {
        return { available: false, reason: `networkstatus threw: ${describeError(error)}` };
    }
}

export async function getEthernetLink (iface = 'eth0') {
    const instance = getNetworkStatusInstance();
    if (!instance) return { available: false, reason: 'networkstatus module unavailable' };

    try {
        const getInterfaceStatus = pickMethod(instance, ['getInterfaceStatus', 'getinterfacestatus']);
        if (!getInterfaceStatus) {
            return { available: false, reason: 'networkstatus getters unavailable on this firmware' };
        }

        const status = await getInterfaceStatus(iface);
        return {
            available: true,
            hasLink: Boolean(status && status.ipAddressList && status.ipAddressList.length)
        };
    } catch (error) {
        return { available: false, reason: `networkstatus threw: ${describeError(error)}` };
    }
}

// Lower metric wins, so Ethernet is preferred over the client Wi-Fi
// Keep in sync with brightsign/server/src/services/network.service.js
const ETHERNET_ROUTING_METRIC = 1;
const WIFI_ROUTING_METRIC = 200;

export const WIFI_INTERFACE_OUTCOME = {
    DISABLED: 'disabled',
    DEPRIORITIZED: 'deprioritized',
    UNCHANGED: 'unchanged',
    UNAVAILABLE: 'unavailable',
    FAILED: 'failed'
};

async function applyRoutingMetric (getConfig, applyConfig, metric) {
    const config = await getConfig();
    if (config.metric === metric) {
        return false;
    }

    config.metric = metric;
    await applyConfig(config);
    return true;
}

export async function disableWifiInterface (iface = 'wlan0', ethernetIface = 'eth0') {
    const NetworkConfiguration = loadNativeModule('@brightsign/networkconfiguration');
    if (!NetworkConfiguration) {
        return {
            outcome: WIFI_INTERFACE_OUTCOME.UNAVAILABLE,
            reason: 'networkconfiguration module unavailable'
        };
    }

    try {
        const network = new NetworkConfiguration(iface);
        const getConfig = pickMethod(network, ['getConfig', 'getconfig']);
        const applyConfig = pickMethod(network, ['applyConfig', 'applyconfig']);
        const ethernetNetwork = new NetworkConfiguration(ethernetIface);
        const getEthernetConfig = pickMethod(ethernetNetwork, ['getConfig', 'getconfig']);
        const applyEthernetConfig = pickMethod(ethernetNetwork, ['applyConfig', 'applyconfig']);

        if (!getConfig || !applyConfig || !getEthernetConfig || !applyEthernetConfig) {
            return {
                outcome: WIFI_INTERFACE_OUTCOME.UNAVAILABLE,
                reason: 'networkconfiguration getters unavailable on this firmware'
            };
        }

        const config = await getConfig();

        if (config.accessPointMode) {
            config.essId = '';
            config.passphrase = '';
            config.accessPointMode = false;
            config.ipAddressList = [];
            config.dnsServerList = [];
            delete config.dhcpServerConfig;

            await applyConfig(config);
            return { outcome: WIFI_INTERFACE_OUTCOME.DISABLED };
        }

        if (config.essId) {
            const ethernetChanged = await applyRoutingMetric(getEthernetConfig, applyEthernetConfig, ETHERNET_ROUTING_METRIC);
            const wifiChanged = await applyRoutingMetric(getConfig, applyConfig, WIFI_ROUTING_METRIC);

            if (ethernetChanged || wifiChanged) {
                return { outcome: WIFI_INTERFACE_OUTCOME.DEPRIORITIZED };
            }
        }

        return { outcome: WIFI_INTERFACE_OUTCOME.UNCHANGED };
    } catch (error) {
        return {
            outcome: WIFI_INTERFACE_OUTCOME.FAILED,
            reason: `networkconfiguration threw: ${describeError(error)}`
        };
    }
}
